import { action, computed, observable } from "mobx";
import debug from "debug";
import { AnyLocation } from "./Location";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { SimpleEventEmitter } from "@utils/SimpleEventEmitter";

export class Navigation {
  afterClose = new SimpleEventEmitter<void>();

  @computed get canGoBack(): boolean {
    return this.stackPos > 0;
  }

  @computed get canGoFwd(): boolean {
    return this.stackPos < this.stack.length - 1;
  }

  @computed get location(): AnyLocation {
    return this.stackPos > -1 ? this.stack[this.stackPos] : null;
  }

  @observable.shallow stack: AnyLocation[] = [];
  @observable stackPos = -1;
  @observable isBlocked = false;

  private readonly log: debug.IDebugger;

  constructor(public name: string, public baseUri: string, private _close: () => void) {
    this.log = debug(`app:NavigationManager:${name}`);
  }

  navigate = (location: AnyLocation) => {
    location.navigation = this;
    this.log("navigate", location);
    this.addToStack(location);
  };

  replace = (location: AnyLocation) => {
    location.navigation = this;
    this.log("replace", location);
    this.replaceLocation(location);
  };

  lockNavigation = (blockID: string, isDirty: boolean, message: string = null) => {
    isDirty ? this.block(blockID, message) : this.unBlock(blockID);
  };

  @action unBlock = (blockID: string) => {
    this.isBlocked = false;
    store().ui.unblockHistory(blockID);
  };

  @action goBack = (showDialog = true) => {
    if (!this.canGoBack) {
      return;
    }

    this.checkIsBlock(() => {
      this.stackPos--;
      this.updateWindowState();
    }, showDialog);
  };

  @action close = (showDialog = true) => {
    this.checkIsBlock(() => {
      this._close();
      this.afterClose.trigger();
    }, showDialog);
  };

  @action forceGoBack = (blockID: string) => {
    if (!this.canGoBack) {
      return;
    }

    this.unBlock(blockID);

    return this.goBack();
  };

  @action goFwd = () => {
    if (!this.canGoFwd) {
      return;
    }
    this.stackPos++;
    this.updateWindowState();
  };

  @action clear = () => {
    this.checkIsBlock(() => {
      this.stack = [];
      this.stackPos = -1;
      this.updateWindowState();
      this.afterClose.trigger();
    });
  };

  private checkIsBlock = (callback: () => any, showDialog = true) => {
    if (this.isBlocked) {
      const lastBlockOperation =
        store().ui.historyBlocks[store().ui.historyBlocks.length - 1];

      if (showDialog) {
        store().ui.confirmLeave(
          lastBlockOperation.message ? lastBlockOperation.message : locale["Leave now?"],
          (confirmed) => {
            if (confirmed) {
              store().ui.clearBlocks();
              this.isBlocked = false;
              callback();
            }
          }
        );
      } else {
        callback();
      }
    } else {
      callback();
    }
  };

  private getNextLocation = (): AnyLocation => {
    return this.stack[this.stackPos + 1];
  };

  private pushNextLocation = (location: AnyLocation) => {
    this.stack.push(location);
    this.stackPos++;
    this.updateWindowState();
  };

  private replaceLocation = (location: AnyLocation) => {
    this.stack[this.stackPos] = location;
    this.updateWindowState(true);
  };

  private trimStackToCurrentPosition = () => {
    this.stack.length = this.stackPos + 1;
  };

  @action private block = (blockID: string, message: string) => {
    this.isBlocked = true;
    store().ui.blockHistory(blockID, message);
  };

  @action private addToStack = (location: AnyLocation) => {
    if (this.canGoFwd) {
      if (this.getNextLocation().equals(location)) {
        this.goFwd();
      } else {
        this.trimStackToCurrentPosition();
        this.pushNextLocation(location);
      }
    } else {
      this.pushNextLocation(location);
    }
  };

  private updateWindowState(replace = false) {
    const uri = this.location ? this.location.toURI() : this.baseUri;
    if (replace) {
      window.history.replaceState(null, "", uri);
    } else {
      window.history.pushState(null, "", uri);
    }
  }
}
